import { Transport, TransportClient, TransportSession } from "@peercolab/engine"
import { peerColabInterceptors } from "./interceptors/peercolab/peerColabInterceptors"
import { backofficeServerInterceptors } from "./interceptors/backofficeServer"
import { mockTranslationInterceptors, TranslationAppender } from "./interceptors/mocks/translationMocks"
import { mockSpeciesInterceptors } from "./interceptors/mocks/speciesMocks"
import { mockBaseCommandsInterceptors } from "./interceptors/mocks/baseCommandsMocks"
import { mockCourseCategoryInterceptors } from "./interceptors/mocks/courseCategoryMocks"
import { mockTrainerInterceptors } from "./interceptors/mocks/trainerMocks"
import { mockHealthCheckItemInterceptors } from "./interceptors/mocks/healthCheckItemMocks"
import { mockInsuranceProviderInterceptors } from "./interceptors/mocks/insuranceProviderMocks"
import { mockSportsInterceptors } from "./interceptors/mocks/sportsMocks"
import { mockPredefinedTasksInterceptors } from "./interceptors/mocks/predefinedTasksMocks"
import { mockCourseInterceptors } from "./interceptors/mocks/courseMocks"

export type APIAuthInfo = {
  isAuthenticated: () => boolean
  getToken: () => Promise<string | undefined>
}

export class Session {
  static session?: TransportSession = undefined

  static authInfo?: APIAuthInfo = undefined
  static devDependenciesEnabled: boolean = false

  public static useDevDependencies() {
    if (!this.devDependenciesEnabled) {
      console.log('Setting up dev dependencies!')
      this.devDependenciesEnabled = true
    }
  }

  public static setAuthInfo(authInfo: APIAuthInfo) {
    this.authInfo = authInfo
  }

  public static getClient(): TransportClient {
    if (!this.session)
      this.initialize()
    return (this.session?.createClient('WebAppClient') as TransportClient)
  }

  public static getSpeciesClient(speciesId: string): TransportClient {
    return this.getClient().addPathParam('speciesId', speciesId)
  }

  public static getBreedClient(speciesId: string, breedId: string): TransportClient {
    return this.getClient().addPathParam('speciesId', speciesId).addPathParam('breedId', breedId)
  }

  public static getCommandClient(commandId: string): TransportClient {
    return this.getClient().addPathParam('commandId', commandId)
  }

  public static getTrainerClient(trainerId: string): TransportClient {
    return this.getClient().addPathParam('trainerId', trainerId)
  }

  public static getPredefinedTaskClient(predefinedTaskId: string): TransportClient {
    return this.getClient().addPathParam('predefinedTaskId', predefinedTaskId)
  }

  public static getCourseClient(courseId: string): TransportClient {
    return this.getClient().addPathParam('courseId', courseId)
  }

  public static getLessonClient(courseId: string, lessonId: string): TransportClient {
    return this.getCourseClient(courseId).addPathParam('lessonId', lessonId)
  }

  static initialize(): void {
    const builder = Transport.session("WebAppSession")

    if (this.devDependenciesEnabled) {
        const translationAppender: TranslationAppender = mockTranslationInterceptors(builder)
        mockSpeciesInterceptors(builder, translationAppender)
        mockBaseCommandsInterceptors(builder, translationAppender)
        mockCourseCategoryInterceptors(builder, translationAppender)
        mockTrainerInterceptors(builder)
        mockHealthCheckItemInterceptors(builder, translationAppender)
        mockInsuranceProviderInterceptors(builder)
        mockSportsInterceptors(builder, translationAppender)
        mockPredefinedTasksInterceptors(builder, translationAppender)
        mockCourseInterceptors(builder)

        //backofficeServerInterceptors(builder, "http://localhost:5101/api", () => ({ isAuthenticated: () => true, getToken: async () => "mock-token" }))
    } else {
        backofficeServerInterceptors(builder, "https://service.backoffice.petkey.no/api", () => this.authInfo)
    }
    peerColabInterceptors(builder)

    this.session = builder
      .inspectRequest(async (input, ctx) => {
        //console.log('running: ' + ctx.operation.id)
      })
      .inspectResponse(async (result, input, ctx) => {
        if (!result.success) {
            console.log(result.error?.toLongString())
        }
        return result
      })
      .build()
  }
}